/**
 * Domain operations used by the UI. Each one writes the local mirror (instant, offline)
 * and queues the change for the server. Server-side triggers recompute authoritative
 * values (due_at, fire_at, completed_at) and handle recurrence after sync.
 */
import {
  DEFAULT_STATUSES,
  DEFAULT_CHANNELS,
  addDays,
  buildDefaultReminderRows,
  completeStatus,
  computeDueAt,
  dateOnlyReminderTime,
  defaultOpenStatus,
  isCompleteType,
  materialiseFireAt,
  positionAfterLast,
  positionBeforeFirst,
  resolveDefaultReminders,
  resolveListStatuses,
  snoozeTarget,
  stateForFireAt,
  instantiateTemplate,
  colorForName,
  type ISODate,
  type ISOTime,
  type List,
  type Priority,
  type Profile,
  type RecurrenceOptions,
  type Reminder,
  type ReminderChannels,
  type ReminderDefault,
  type SnoozeOption,
  type Status,
  type StatusType,
  type Tag,
  type Task,
  type TemplateDefinition,
  type NotificationLog,
} from '@karya/core';
import { db } from '@/db/dexie';
import { newId } from '@/lib/ids';
import { currentUserId } from '@/state/session';
import { fallbackProfile } from './queries';
import { nowIso, patchRow, patchRows, putRows, restore, softDelete } from './write';

// ---------------------------------------------------------------------------
// context helpers
// ---------------------------------------------------------------------------

async function profile(): Promise<Profile> {
  const uid = currentUserId();
  return (await db().profiles.get(uid)) ?? fallbackProfile(uid, null);
}

function base(ts = nowIso()) {
  return { user_id: currentUserId(), created_at: ts, updated_at: ts, deleted_at: null };
}

async function statusesForList(listId: string): Promise<Status[]> {
  const d = db();
  const list = await d.lists.get(listId);
  if (!list) return [];
  const all = await d.statuses.toArray();
  return resolveListStatuses(list, all);
}

function dueAtFor(p: Profile, date: ISODate | null, time: ISOTime | null): string | null {
  return computeDueAt(date, time, p.timezone, dateOnlyReminderTime(p));
}

// ---------------------------------------------------------------------------
// tasks
// ---------------------------------------------------------------------------

export interface NewTaskInput {
  name: string;
  list_id: string;
  parent_task_id?: string | null;
  status_id?: string | null;
  due_date?: ISODate | null;
  due_time?: ISOTime | null;
  start_date?: ISODate | null;
  priority?: Priority | null;
  description_md?: string | null;
  tag_ids?: string[];
  recurrence?: RecurrenceOptions | null;
  recurrence_rrule?: string | null;
  /** 'default' applies list/profile defaults when the task has a due date. */
  reminders?: 'default' | 'none' | ReminderDefault[];
  position?: number;
}

export async function createTask(input: NewTaskInput): Promise<Task> {
  const d = db();
  const p = await profile();
  const statuses = await statusesForList(input.list_id);
  const status = input.status_id
    ? statuses.find((s) => s.id === input.status_id)
    : defaultOpenStatus(statuses);
  if (!status) throw new Error('This list has no statuses yet.');
  const siblings = await d.tasks.where('list_id').equals(input.list_id).toArray();
  const position =
    input.position ??
    positionAfterLast(
      siblings
        .filter((t) => (t.parent_task_id ?? null) === (input.parent_task_id ?? null))
        .map((t) => t.position),
    );
  const due_date = input.due_date ?? null;
  const due_time = input.due_time ?? null;
  const due_at = dueAtFor(p, due_date, due_time);
  const ts = nowIso();
  const reminderMode = input.reminders ?? 'default';
  const task: Task = {
    ...base(ts),
    id: newId(),
    list_id: input.list_id,
    parent_task_id: input.parent_task_id ?? null,
    name: input.name.trim(),
    description_md: input.description_md ?? null,
    status_id: status.id,
    priority: input.priority ?? null,
    start_date: input.start_date ?? null,
    start_time: null,
    due_date,
    due_time,
    due_at,
    completed_at: isCompleteType(status.type) ? ts : null,
    position,
    recurrence: input.recurrence ?? null,
    recurrence_rrule: input.recurrence_rrule ?? null,
    recurrence_parent_id: null,
    archived_at: null,
    source_url: null,
    clickup_id: null,
    reminders_initialized: reminderMode !== 'default' || due_at !== null,
  };
  await putRows('tasks', [task as unknown as Record<string, unknown>]);
  if (input.tag_ids?.length) {
    await putRows(
      'task_tags',
      input.tag_ids.map((tag_id) => ({ ...base(ts), task_id: task.id, tag_id })),
    );
  }
  if (due_at) {
    if (reminderMode === 'default') {
      const list = await d.lists.get(task.list_id);
      const { defaults, source } = resolveDefaultReminders(list, p);
      await insertReminderRows(
        task.id,
        buildDefaultReminderRows(defaults, source, due_at, new Date()),
      );
    } else if (Array.isArray(reminderMode)) {
      await insertReminderRows(
        task.id,
        buildDefaultReminderRows(reminderMode, 'manual', due_at, new Date()),
      );
    }
  }
  return task;
}

async function insertReminderRows(
  taskId: string | null,
  rows: Pick<
    Reminder,
    'kind' | 'at' | 'offset_minutes' | 'fire_at' | 'channels' | 'state' | 'source' | 'nag_sent'
  >[],
): Promise<void> {
  if (rows.length === 0) return;
  const ts = nowIso();
  await putRows(
    'reminders',
    rows.map((r) => ({
      ...base(ts),
      id: newId(),
      task_id: taskId,
      title: null,
      nag_every_minutes: null,
      nag_until: null,
      nag_count: null,
      snoozed_until: null,
      ...r,
    })),
  );
}

export type TaskPatch = Partial<
  Pick<
    Task,
    | 'name'
    | 'description_md'
    | 'priority'
    | 'start_date'
    | 'start_time'
    | 'due_date'
    | 'due_time'
    | 'position'
    | 'recurrence'
    | 'recurrence_rrule'
    | 'archived_at'
    | 'parent_task_id'
    | 'list_id'
  >
>;

export async function updateTask(id: string, patch: TaskPatch): Promise<void> {
  const d = db();
  const task = await d.tasks.get(id);
  if (!task) return;
  const full: Partial<Task> = { ...patch };
  const dueChanged = 'due_date' in patch || 'due_time' in patch;
  if (dueChanged) {
    const p = await profile();
    const date = 'due_date' in patch ? (patch.due_date ?? null) : task.due_date;
    const time = date ? ('due_time' in patch ? (patch.due_time ?? null) : task.due_time) : null;
    full.due_time = time;
    full.due_at = dueAtFor(p, date, time);
    if (full.due_at && !task.reminders_initialized) {
      full.reminders_initialized = true;
      const list = await d.lists.get(task.list_id);
      const { defaults, source } = resolveDefaultReminders(list, p);
      await patchRow<Task>('tasks', id, full);
      await insertReminderRows(
        id,
        buildDefaultReminderRows(defaults, source, full.due_at, new Date()),
      );
      return;
    }
    await patchRow<Task>('tasks', id, full);
    await rematerialiseRelative(id, full.due_at ?? null);
    return;
  }
  await patchRow<Task>('tasks', id, full);
}

/** Local mirror of the server trigger that moves relative reminders with the due date. */
async function rematerialiseRelative(taskId: string, dueAt: string | null): Promise<void> {
  const rows = (await db().reminders.where('task_id').equals(taskId).toArray()).filter(
    (r) => !r.deleted_at && r.kind === 'relative',
  );
  const now = new Date();
  await patchRows<Reminder>(
    'reminders',
    rows.map((r) => {
      const fire = materialiseFireAt(r, dueAt);
      return {
        key: r.id,
        patch: {
          fire_at: fire ?? r.fire_at,
          state: stateForFireAt(fire, now),
          snoozed_until: null,
          nag_sent: 0,
        },
      };
    }),
  );
}

export async function setTaskStatus(
  id: string,
  statusId: string,
): Promise<{ completed: boolean; recurring: boolean }> {
  const d = db();
  const [task, status] = await Promise.all([d.tasks.get(id), d.statuses.get(statusId)]);
  if (!task || !status) return { completed: false, recurring: false };
  const prev = await d.statuses.get(task.status_id);
  const wasComplete = isCompleteType(prev?.type);
  const nowComplete = isCompleteType(status.type);
  const patch: Partial<Task> = { status_id: statusId } as Partial<Task>;
  if (nowComplete && !wasComplete) patch.completed_at = nowIso();
  if (!nowComplete && wasComplete) patch.completed_at = null;
  await patchRow<Task>('tasks', id, patch);
  if (nowComplete && !wasComplete) {
    const pending = (await d.reminders.where('task_id').equals(id).toArray()).filter(
      (r) => !r.deleted_at && (r.state === 'scheduled' || r.state === 'snoozed'),
    );
    await patchRows<Reminder>(
      'reminders',
      pending.map((r) => ({ key: r.id, patch: { state: 'cancelled' } })),
    );
  }
  if (!nowComplete && wasComplete) await rematerialiseRelative(id, task.due_at);
  return { completed: nowComplete && !wasComplete, recurring: Boolean(task.recurrence_rrule) };
}

export async function completeTask(id: string): Promise<{ recurring: boolean } | null> {
  const task = await db().tasks.get(id);
  if (!task) return null;
  const target = completeStatus(await statusesForList(task.list_id));
  if (!target) return null;
  const res = await setTaskStatus(id, target.id);
  return { recurring: res.recurring };
}

export async function reopenTask(id: string): Promise<void> {
  const task = await db().tasks.get(id);
  if (!task) return;
  const target = defaultOpenStatus(await statusesForList(task.list_id));
  if (target) await setTaskStatus(id, target.id);
}

export async function toggleComplete(
  task: Task,
): Promise<{ completed: boolean; recurring: boolean }> {
  const status = await db().statuses.get(task.status_id);
  if (isCompleteType(status?.type)) {
    await reopenTask(task.id);
    return { completed: false, recurring: false };
  }
  const r = await completeTask(task.id);
  return { completed: true, recurring: r?.recurring ?? false };
}

/** Move a task (and its subtasks) to another list, mapping the status by name, then type. */
export async function moveTask(id: string, listId: string): Promise<void> {
  const d = db();
  const task = await d.tasks.get(id);
  if (!task || task.list_id === listId) return;
  const [from, to] = await Promise.all([statusesForList(task.list_id), statusesForList(listId)]);
  const mapStatus = (statusId: string) => {
    const s = from.find((x) => x.id === statusId);
    return (
      to.find((x) => s && x.name.toLowerCase() === s.name.toLowerCase()) ??
      to.find((x) => s && x.type === s.type) ??
      defaultOpenStatus(to)
    )?.id;
  };
  const targets = [task, ...(await d.tasks.where('parent_task_id').equals(id).toArray())];
  const destPositions = (await d.tasks.where('list_id').equals(listId).toArray()).map(
    (t) => t.position,
  );
  await patchRows<Task>(
    'tasks',
    targets.map((t) => ({
      key: t.id,
      patch: {
        list_id: listId,
        status_id: mapStatus(t.status_id) ?? t.status_id,
        ...(t.id === id ? { position: positionAfterLast(destPositions) } : {}),
      },
    })),
  );
}

export async function reorderTask(id: string, position: number, statusId?: string): Promise<void> {
  if (statusId) {
    const task = await db().tasks.get(id);
    if (task && task.status_id !== statusId) await setTaskStatus(id, statusId);
  }
  await patchRow<Task>('tasks', id, { position });
}

export async function duplicateTask(id: string): Promise<Task | null> {
  const d = db();
  const task = await d.tasks.get(id);
  if (!task) return null;
  const tagIds = (await d.task_tags.where('task_id').equals(id).toArray())
    .filter((t) => !t.deleted_at)
    .map((t) => t.tag_id);
  return createTask({
    name: `${task.name} (copy)`,
    list_id: task.list_id,
    parent_task_id: task.parent_task_id,
    status_id: task.status_id,
    due_date: task.due_date,
    due_time: task.due_time,
    start_date: task.start_date,
    priority: task.priority,
    description_md: task.description_md,
    tag_ids: tagIds,
    position: task.position + 0.5,
    recurrence: task.recurrence,
    recurrence_rrule: task.recurrence_rrule,
  });
}

/** Soft-delete a task with its subtasks (Trash keeps them 30 days). */
export async function deleteTask(id: string): Promise<string[]> {
  const subs = (await db().tasks.where('parent_task_id').equals(id).toArray()).filter(
    (t) => !t.deleted_at,
  );
  const ids = [id, ...subs.map((s) => s.id)];
  await softDelete('tasks', ids);
  return ids;
}

export async function restoreTasks(ids: string[]): Promise<void> {
  await restore('tasks', ids);
}

export async function archiveTask(id: string, archived = true): Promise<void> {
  await patchRow<Task>('tasks', id, { archived_at: archived ? nowIso() : null });
}

export async function setPriority(id: string, priority: Priority | null): Promise<void> {
  await patchRow<Task>('tasks', id, { priority });
}

/** Quick date picks used by menus, snooze-reschedule and keyboard shortcuts. */
export function quickDate(
  kind: 'today' | 'tomorrow' | 'weekend' | 'next_week' | 'two_weeks' | 'four_weeks',
  today: ISODate,
): ISODate {
  const dow = new Date(`${today}T00:00:00Z`).getUTCDay();
  switch (kind) {
    case 'today':
      return today;
    case 'tomorrow':
      return addDays(today, 1);
    case 'weekend':
      return addDays(today, dow === 6 ? 0 : (6 - dow + 7) % 7 || 7);
    case 'next_week':
      return addDays(today, (8 - dow) % 7 || 7);
    case 'two_weeks':
      return addDays(today, 14);
    case 'four_weeks':
      return addDays(today, 28);
  }
}

// ---------------------------------------------------------------------------
// tags
// ---------------------------------------------------------------------------

export async function setTaskTag(taskId: string, tagId: string, on: boolean): Promise<void> {
  const d = db();
  const existing = await d.task_tags.get([taskId, tagId]);
  if (existing) {
    if (on === !existing.deleted_at) return;
    await patchRow('task_tags', [taskId, tagId], { deleted_at: on ? null : nowIso() });
    return;
  }
  if (on) await putRows('task_tags', [{ ...base(), task_id: taskId, tag_id: tagId }]);
}

export async function createTag(spaceId: string, name: string, color?: string): Promise<Tag> {
  const existing = (await db().tags.where('space_id').equals(spaceId).toArray()).find(
    (t) => !t.deleted_at && t.name.toLowerCase() === name.trim().toLowerCase(),
  );
  if (existing) return existing;
  const tag: Tag = {
    ...base(),
    id: newId(),
    space_id: spaceId,
    name: name.trim(),
    color: color ?? colorForName(name),
  };
  await putRows('tags', [tag as unknown as Record<string, unknown>]);
  return tag;
}

export async function updateTag(
  id: string,
  patch: Partial<Pick<Tag, 'name' | 'color'>>,
): Promise<void> {
  await patchRow<Tag>('tags', id, patch);
}

export async function deleteTag(id: string): Promise<void> {
  await softDelete('tags', [id]);
}

// ---------------------------------------------------------------------------
// checklists
// ---------------------------------------------------------------------------

export async function addChecklist(taskId: string, name = 'Checklist'): Promise<string> {
  const existing = await db().checklists.where('task_id').equals(taskId).toArray();
  const id = newId();
  await putRows('checklists', [
    {
      ...base(),
      id,
      task_id: taskId,
      name,
      position: positionAfterLast(existing.map((c) => c.position)),
      clickup_id: null,
    },
  ]);
  return id;
}

export async function renameChecklist(id: string, name: string): Promise<void> {
  await patchRow('checklists', id, { name });
}

export async function deleteChecklist(id: string): Promise<void> {
  await softDelete('checklists', [id]);
}

export async function addChecklistItem(checklistId: string, name: string): Promise<void> {
  const existing = await db().checklist_items.where('checklist_id').equals(checklistId).toArray();
  await putRows('checklist_items', [
    {
      ...base(),
      id: newId(),
      checklist_id: checklistId,
      name,
      done: false,
      position: positionAfterLast(existing.map((c) => c.position)),
      clickup_id: null,
    },
  ]);
}

export async function updateChecklistItem(
  id: string,
  patch: { name?: string; done?: boolean },
): Promise<void> {
  await patchRow('checklist_items', id, patch);
}

export async function deleteChecklistItem(id: string): Promise<void> {
  await softDelete('checklist_items', [id]);
}

// ---------------------------------------------------------------------------
// reminders
// ---------------------------------------------------------------------------

export interface ReminderInput {
  kind: 'absolute' | 'relative';
  at?: string | null;
  offset_minutes?: number | null;
  title?: string | null;
  channels?: ReminderChannels;
  nag_every_minutes?: number | null;
  nag_until?: 'task_complete' | 'count' | null;
  nag_count?: number | null;
}

export async function addReminder(
  taskId: string | null,
  input: ReminderInput,
): Promise<string | null> {
  const task = taskId ? await db().tasks.get(taskId) : undefined;
  const fire = materialiseFireAt(
    { kind: input.kind, at: input.at ?? null, offset_minutes: input.offset_minutes ?? null },
    task?.due_at ?? null,
  );
  if (!fire) return null;
  const id = newId();
  if (taskId && task && !task.reminders_initialized)
    await patchRow<Task>('tasks', taskId, { reminders_initialized: true });
  await putRows('reminders', [
    {
      ...base(),
      id,
      task_id: taskId,
      title: input.title ?? null,
      kind: input.kind,
      at: input.kind === 'absolute' ? (input.at ?? null) : null,
      offset_minutes: input.kind === 'relative' ? (input.offset_minutes ?? 0) : null,
      fire_at: fire,
      channels: input.channels ?? { ...DEFAULT_CHANNELS },
      nag_every_minutes: input.nag_every_minutes ?? null,
      nag_until: input.nag_every_minutes ? (input.nag_until ?? 'task_complete') : null,
      nag_count: input.nag_count ?? null,
      nag_sent: 0,
      snoozed_until: null,
      state: stateForFireAt(fire, new Date()),
      source: 'manual',
    },
  ]);
  return id;
}

export async function updateReminder(id: string, input: Partial<ReminderInput>): Promise<void> {
  const d = db();
  const r = await d.reminders.get(id);
  if (!r) return;
  const next = { ...r, ...input } as Reminder;
  const task = r.task_id ? await d.tasks.get(r.task_id) : undefined;
  const fire = materialiseFireAt(next, task?.due_at ?? null) ?? r.fire_at;
  await patchRow<Reminder>('reminders', id, {
    ...(input as Partial<Reminder>),
    nag_until: next.nag_every_minutes ? (next.nag_until ?? 'task_complete') : null,
    fire_at: fire,
    state: stateForFireAt(fire, new Date()),
    snoozed_until: null,
    nag_sent: 0,
  });
}

export async function deleteReminder(id: string): Promise<void> {
  await softDelete('reminders', [id]);
}

export async function snoozeReminder(id: string, option: SnoozeOption): Promise<Date> {
  const p = await profile();
  const until = snoozeTarget(option, new Date(), p.timezone, p.day_start_time.slice(0, 5));
  await patchRow<Reminder>('reminders', id, {
    state: 'snoozed',
    snoozed_until: until.toISOString(),
  });
  return until;
}

export async function markReminderDone(id: string): Promise<void> {
  await patchRow<Reminder>('reminders', id, { state: 'done' });
}

/** Snooze from a task row: snoozes the task's next due reminder, or creates one. */
export async function snoozeTask(taskId: string, option: SnoozeOption): Promise<Date> {
  const p = await profile();
  const until = snoozeTarget(option, new Date(), p.timezone, p.day_start_time.slice(0, 5));
  const rows = (await db().reminders.where('task_id').equals(taskId).toArray()).filter(
    (r) => !r.deleted_at,
  );
  const target =
    rows.find((r) => r.state === 'fired' || r.state === 'snoozed') ??
    rows
      .filter((r) => r.state === 'scheduled')
      .sort((a, b) => Date.parse(a.fire_at) - Date.parse(b.fire_at))[0];
  if (target) {
    await patchRow<Reminder>('reminders', target.id, {
      state: 'snoozed',
      snoozed_until: until.toISOString(),
    });
  } else {
    await addReminder(taskId, { kind: 'absolute', at: until.toISOString() });
  }
  return until;
}

// ---------------------------------------------------------------------------
// notifications (Inbox)
// ---------------------------------------------------------------------------

export async function markNotificationsRead(ids: string[]): Promise<void> {
  const ts = nowIso();
  await patchRows<NotificationLog>(
    'notifications_log',
    ids.map((key) => ({ key, patch: { read_at: ts } })),
  );
}

export async function setNotificationActed(
  id: string,
  acted: NotificationLog['acted'],
): Promise<void> {
  await patchRow<NotificationLog>('notifications_log', id, { acted, read_at: nowIso() });
}

export async function clearNotifications(ids: string[]): Promise<void> {
  await softDelete('notifications_log', ids);
}

// ---------------------------------------------------------------------------
// hierarchy
// ---------------------------------------------------------------------------

async function ensureWorkspace(): Promise<string> {
  const ws = (await db().workspaces.toArray()).find((w) => !w.deleted_at);
  if (ws) return ws.id;
  const id = newId();
  await putRows('workspaces', [{ ...base(), id, name: 'My Workspace', icon: null }]);
  return id;
}

export async function createSpace(name: string, color: string): Promise<string> {
  const d = db();
  const workspaceId = await ensureWorkspace();
  const spaces = await d.spaces.toArray();
  const id = newId();
  const ts = nowIso();
  await putRows('spaces', [
    {
      ...base(ts),
      id,
      workspace_id: workspaceId,
      name,
      color,
      icon: null,
      position: positionAfterLast(spaces.map((s) => s.position)),
      archived_at: null,
      clickup_id: null,
    },
  ]);
  await putRows(
    'statuses',
    DEFAULT_STATUSES.map((s, i) => ({
      ...base(ts),
      id: newId(),
      space_id: id,
      list_id: null,
      name: s.name,
      color: s.color,
      type: s.type,
      position: (i + 1) * 1024,
    })),
  );
  return id;
}

export async function updateSpace(
  id: string,
  patch: {
    name?: string;
    color?: string;
    icon?: string | null;
    archived_at?: string | null;
    position?: number;
  },
): Promise<void> {
  await patchRow('spaces', id, patch);
}

export async function deleteSpace(id: string): Promise<void> {
  const d = db();
  const lists = (await d.lists.where('space_id').equals(id).toArray()).filter((l) => !l.deleted_at);
  for (const l of lists) await deleteList(l.id);
  const folders = (await d.folders.where('space_id').equals(id).toArray()).filter(
    (f) => !f.deleted_at,
  );
  await softDelete(
    'folders',
    folders.map((f) => f.id),
  );
  await softDelete('spaces', [id]);
}

export async function createFolder(spaceId: string, name: string): Promise<string> {
  const folders = await db().folders.where('space_id').equals(spaceId).toArray();
  const id = newId();
  await putRows('folders', [
    {
      ...base(),
      id,
      space_id: spaceId,
      name,
      position: positionAfterLast(folders.map((f) => f.position)),
      archived_at: null,
      clickup_id: null,
    },
  ]);
  return id;
}

export async function updateFolder(
  id: string,
  patch: { name?: string; archived_at?: string | null; position?: number },
): Promise<void> {
  await patchRow('folders', id, patch);
}

export async function deleteFolder(id: string): Promise<void> {
  const lists = (await db().lists.where('folder_id').equals(id).toArray()).filter(
    (l) => !l.deleted_at,
  );
  for (const l of lists) await deleteList(l.id);
  await softDelete('folders', [id]);
}

export async function createList(
  spaceId: string,
  name: string,
  opts: { folderId?: string | null; emoji?: string | null; color?: string | null } = {},
): Promise<string> {
  const lists = await db().lists.where('space_id').equals(spaceId).toArray();
  const id = newId();
  await putRows('lists', [
    {
      ...base(),
      id,
      space_id: spaceId,
      folder_id: opts.folderId ?? null,
      name,
      emoji: opts.emoji ?? null,
      color: opts.color ?? null,
      position: positionAfterLast(lists.map((l) => l.position)),
      archived_at: null,
      override_statuses: false,
      default_reminders: null,
      clickup_id: null,
    },
  ]);
  return id;
}

export async function updateList(
  id: string,
  patch: Partial<
    Pick<
      List,
      | 'name'
      | 'emoji'
      | 'color'
      | 'archived_at'
      | 'position'
      | 'folder_id'
      | 'space_id'
      | 'default_reminders'
      | 'override_statuses'
    >
  >,
): Promise<void> {
  await patchRow<List>('lists', id, patch);
}

export async function deleteList(id: string): Promise<void> {
  const tasks = (await db().tasks.where('list_id').equals(id).toArray()).filter(
    (t) => !t.deleted_at,
  );
  await softDelete(
    'tasks',
    tasks.map((t) => t.id),
  );
  await softDelete('lists', [id]);
}

/** Give a list its own status set (copied from the space) so it can be edited separately. */
export async function overrideListStatuses(listId: string): Promise<void> {
  const d = db();
  const list = await d.lists.get(listId);
  if (!list || list.override_statuses) return;
  const spaceStatuses = (await d.statuses.where('space_id').equals(list.space_id).toArray()).filter(
    (s) => !s.deleted_at,
  );
  const map = new Map<string, string>();
  const ts = nowIso();
  const copies = spaceStatuses.map((s) => {
    const id = newId();
    map.set(s.id, id);
    return {
      ...base(ts),
      id,
      space_id: null,
      list_id: listId,
      name: s.name,
      color: s.color,
      type: s.type,
      position: s.position,
    };
  });
  await putRows('statuses', copies);
  await updateList(listId, { override_statuses: true });
  const tasks = (await d.tasks.where('list_id').equals(listId).toArray()).filter((t) =>
    map.has(t.status_id),
  );
  await patchRows<Task>(
    'tasks',
    tasks.map((t) => ({ key: t.id, patch: { status_id: map.get(t.status_id)! } })),
  );
}

export async function createStatus(
  scope: { space_id?: string; list_id?: string },
  name: string,
  type: StatusType,
  color: string,
): Promise<string> {
  const d = db();
  const all = (await d.statuses.toArray()).filter(
    (s) =>
      !s.deleted_at &&
      (scope.list_id ? s.list_id === scope.list_id : s.space_id === scope.space_id),
  );
  const id = newId();
  await putRows('statuses', [
    {
      ...base(),
      id,
      space_id: scope.list_id ? null : (scope.space_id ?? null),
      list_id: scope.list_id ?? null,
      name: name.toUpperCase(),
      color,
      type,
      position: positionAfterLast(all.filter((s) => s.type === type).map((s) => s.position)),
    },
  ]);
  return id;
}

export async function updateStatus(
  id: string,
  patch: Partial<Pick<Status, 'name' | 'color' | 'type' | 'position'>>,
): Promise<void> {
  await patchRow<Status>('statuses', id, {
    ...patch,
    ...(patch.name ? { name: patch.name.toUpperCase() } : {}),
  });
}

/** Delete a status, moving its tasks to `replacementId`. */
export async function deleteStatus(id: string, replacementId: string): Promise<void> {
  const tasks = (await db().tasks.where('status_id').equals(id).toArray()).filter(
    (t) => !t.deleted_at,
  );
  for (const t of tasks) await setTaskStatus(t.id, replacementId);
  await softDelete('statuses', [id]);
}

// ---------------------------------------------------------------------------
// profile & onboarding
// ---------------------------------------------------------------------------

export async function updateProfile(patch: Partial<Profile>): Promise<void> {
  const uid = currentUserId();
  const d = db();
  const existing = await d.profiles.get(uid);
  if (!existing) {
    const row = { ...fallbackProfile(uid, null), ...patch, updated_at: nowIso() };
    await putRows('profiles', [row as unknown as Record<string, unknown>]);
    return;
  }
  await patchRow<Profile>('profiles', uid, patch);
}

/** Create spaces/lists/statuses/tags from a template and finish onboarding. */
export async function applyTemplate(
  def: TemplateDefinition,
  opts: { spaceNames?: string[]; dayStart: string; timezone: string },
): Promise<void> {
  const d = db();
  const existingWs = (await d.workspaces.toArray()).find((w) => !w.deleted_at);
  const out = instantiateTemplate(def, {
    userId: currentUserId(),
    newId,
    now: new Date(),
    spaceNames: opts.spaceNames,
    workspaceId: existingWs?.id,
    workspaceName: 'My Workspace',
  });
  if (out.workspace)
    await putRows('workspaces', [out.workspace as unknown as Record<string, unknown>]);
  await putRows('spaces', out.spaces as unknown as Record<string, unknown>[]);
  await putRows('lists', out.lists as unknown as Record<string, unknown>[]);
  await putRows('statuses', out.statuses as unknown as Record<string, unknown>[]);
  await putRows('tags', out.tags as unknown as Record<string, unknown>[]);
  await updateProfile({
    default_quick_add_list_id: out.quickAddListId,
    day_start_time: opts.dayStart,
    timezone: opts.timezone,
    default_reminders: def.default_reminders ?? null,
    onboarded_at: nowIso(),
  });
}

export { positionBeforeFirst };
